<template>
  <el-row type="flex" class="w-full">
    <el-col>
      <el-row v-if="!audioFile" type="flex" justify="center" class="w-full">
        <el-button
          class="w-full"
          :type="recording ? 'danger' : 'success'"
          @click="toggleRecord"
          round
        >
          {{ recording ? `${recordingTime} - Press to review` : "Press to talk" }}
        </el-button>
      </el-row>
      <el-row v-else type="flex" justify="center" class="w-full">
        <audio class="h-[30px]" controls :src="audioURL"></audio>
      </el-row>
    </el-col>
  </el-row>
</template>

<script setup>
/**
 * @fileoverview AudioRecorder Component - Voice Message Recording (Vue 3)
 *
 * Provides audio recording functionality for voice messages in the webchat.
 * Uses the MediaRecorder API to capture audio from the user's microphone.
 *
 * @component AudioRecorder
 * @displayName Audio Recorder
 *
 * Features:
 * - Start/stop audio recording
 * - Recording time display with countdown
 * - Audio preview playback
 * - Auto-stop after 30 seconds
 * - Microphone permission handling
 *
 * @vue3migration
 * - Migrated from Options API to Composition API with script setup
 * - Uses defineProps and defineEmits for component interface
 * - Uses ref for reactive state
 * - Uses defineExpose to expose cancelAudio method to parent
 */

import { ref } from 'vue'
import { ElNotification } from 'element-plus'

defineOptions({
  name: 'AudioRecorder',
})

const props = defineProps({
  audioFile: {
    type: [Blob, null],
    default: null,
  },
})

const emit = defineEmits(['updateAudioFile'])

// Reactive state
const mediaRecorder = ref(null)
const chunks = ref([])
const recording = ref(false)
const audioURL = ref(null)
const startTime = ref(null)
const recordingTime = ref('00:00')
const interval = ref(null)

/**
 * Toggle between start and stop recording
 */
function toggleRecord() {
  if (!recording.value) {
    startRecording()
  } else {
    stopRecording()
  }
}

/**
 * Start audio recording
 * Requests microphone permission and initializes MediaRecorder
 */
function startRecording() {
  startTime.value = new Date()
  updateRecordingTime()
  navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => {
      mediaRecorder.value = new MediaRecorder(stream)
      mediaRecorder.value.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.value.push(e.data)
        }
      }
      mediaRecorder.value.start()
      recording.value = true
    })
    .catch((err) => {
      console.error('Error accessing microphone:', err)
      ElNotification.error({
        title: 'Error',
        position: 'bottom-right',
        message: 'Error accessing microphone',
      })
    })
}

/**
 * Stop audio recording and create audio blob
 */
function stopRecording() {
  if (!mediaRecorder.value) {
    return
  }
  mediaRecorder.value.stop()
  recording.value = false

  // Wait for all data to be available before creating the Blob
  const waitForData = new Promise((resolve) => {
    mediaRecorder.value.onstop = resolve
  })

  waitForData.then(() => {
    const audioFileBlob = new Blob(chunks.value, { type: 'audio/wav' })
    emit('updateAudioFile', audioFileBlob)
    audioURL.value = URL.createObjectURL(audioFileBlob)

    // Close the media stream to release the microphone
    mediaRecorder.value.stream.getTracks().forEach((track) => track.stop())

    // Clear interval
    clearInterval(interval.value)
  })
}

/**
 * Toggle recording state (alias for toggleRecord)
 */
function toggleRecording() {
  if (!recording.value) {
    startRecording()
  } else {
    stopRecording()
  }
}

/**
 * Update the recording time display
 * Auto-stops recording after 30 seconds
 */
function updateRecordingTime() {
  interval.value = setInterval(() => {
    const currentTime = new Date()
    const elapsedTime = new Date(currentTime - startTime.value)
    if (elapsedTime > 0) {
      recordingTime.value = elapsedTime.toISOString().substr(14, 5)
      // Stop recording after 30 seconds
      if (elapsedTime >= 30000) {
        // 30 seconds in milliseconds
        clearInterval(interval.value)
        stopRecording()
      }
    }
  }, 1000)
}

/**
 * Cancel and reset audio recording
 * Exposed to parent component for external control
 */
function cancelAudio() {
  mediaRecorder.value = null
  chunks.value = []
  emit('updateAudioFile', null)
  recording.value = false
  audioURL.value = null
  startTime.value = null
  recordingTime.value = '00:00'
  interval.value = null
}

// Expose cancelAudio method to parent component
defineExpose({
  cancelAudio,
})
</script>
